import { useEffect, useState, useContext } from "react"

//PrimeReact
import { Accordion, AccordionTab } from "primereact/accordion"
import { ProgressSpinner } from "primereact/progressspinner"
import { Paginator } from "primereact/paginator"

//Tasks modules
import { taskApiGetCreatedTasksPOST } from "../TasksApi"

// Context
import { UserContext } from "../../../context/userContext"

//Helpers
import { LogDebug } from "../../../helpers/logger"
import TaskDisplayPanelCreated from "./TaskDisplayPanelCreated"

let lastRefresh = {
  pending: new Date().getTime(),
  completed: new Date().getTime(),
}

export default function TasksCreated(props) {
  const userCtx = useContext(UserContext)

  const [loading, setLoading] = useState(false)
  const [pendingPageSize, setPendingPageSize] = useState(10)
  const [pendingCreatedTasks, setPendingCreatedTasks] = useState(null)
  const [completedCreatedTasks, setCompletedCreatedTasks] = useState(null)
  const [completedPageSize, setCompletedPageSize] = useState(10)
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1)
  const [completedCurrentPage, setCompletedCurrentPage] = useState(1)
  const [activePanel, setActivePanel] = useState(0)

  useEffect(() => {
    if (!pendingCreatedTasks) {
      getCreatedTasks(1, pendingPageSize, "pending")
    } else if (!completedCreatedTasks) {
      getCreatedTasks(1, completedPageSize, "completed")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCreatedTasks, completedCreatedTasks])

  useEffect(() => {
    setActivePanel(props.activeOptionPanel || 0)
  }, [props.activeOptionPanel])

  useEffect(() => {
    setActivePanel(0)
  }, [props.showCreateForm])

  useEffect(() => {
    if (props.refreshTasks > lastRefresh["pending"]) {
      getCreatedTasks(1, pendingPageSize, "pending")
    }
    if (props.refreshTasks > lastRefresh["completed"]) {
      getCreatedTasks(1, completedPageSize, "complete")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.refreshTasks])

  useEffect(() => {
    if (props.addTask) {
      props.setAddTask("")
      closeEditAdd()
      setTimeout(() => {
        addEditTask()
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.addTask])

  async function getCreatedTasks(page, pageSize, ...types) {
    setLoading(true)

    for (const type of types) {
      try {
        // Limit refreshes
        if (new Date().getTime() - lastRefresh[type] < 1000) continue
        lastRefresh[type] = new Date().getTime()

        const res = await taskApiGetCreatedTasksPOST(userCtx.tokenTask, {
          groupId: userCtx.session.user.organization_id,
          userId: userCtx.session.user.staff_id,
          pageNum:
            page ||
            (props.accordionKey === "0"
              ? pendingCurrentPage
              : completedCurrentPage),
          pageSize: pageSize
            ? pageSize
            : type === "pending"
            ? pendingPageSize
            : completedPageSize,
          status: type,
        })

        if (res) {
          if (type === "pending") setPendingCreatedTasks(res)
          else setCompletedCreatedTasks(res)
        }

        if (res.numTotal > 0) {
          props.checkUserCache(res.items.map((x) => x.createdByUserId))
        }

        LogDebug(`Created Tasks [${type}]`, res)
      } catch (error) {
        console.error(`Unable to list ${type} created tasks`, error)
        props.onShowError("Unable to list tasks. See console for details.")
      }
    }
    setLoading(false)
  }

  const onPendingPageChange = (event) => {
    if (pendingPageSize !== event.rows) {
      setPendingPageSize(event.rows)
    }
    setPendingCurrentPage(event.page + 1)

    getCreatedTasks(event.page + 1, event.rows, "pending")
  }

  const onCompletedPageChange = (event) => {
    if (completedPageSize !== event.rows) {
      setCompletedPageSize(event.rows)
    }

    setCompletedCurrentPage(event.page + 1)

    getCreatedTasks(event.page + 1, event.rows, "complete")
  }

  function closeEditAdd() {
    props.onSetShowForm(false)
    //ReactTooltip.rebuild(); // Rebuild tooltips when things change
  }

  function addEditTask(taskId) {
    props.setAccordionKey("0")
    props.onSetShowCreateForm(true)
    setTimeout(() => {
      if (taskId) {
        props.onSetEditTask(
          JSON.parse(
            JSON.stringify(
              pendingCreatedTasks.items.find((x) => x.id === taskId)
            )
          )
        )
      } else {
        props.onSetEditTask(null)
      }
    }, 250)
  }

  return (
    <div>
      {loading || props.parentLoading ? (
        <div style={{ textAlign: "center" }}>
          <ProgressSpinner />
        </div>
      ) : (
        <div style={{ width: "100%" }}>
          <Accordion
            activeIndex={activePanel}
            onTabChange={(e) => {
              setActivePanel(e.index)
            }}
            style={{ width: "100%" }}
          >
            <AccordionTab
              header={`Pending tasks [${
                pendingCreatedTasks ? pendingCreatedTasks.items.length : 0
              }]`}
              style={{ padding: 0 }}
            >
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div
                  style={{
                    flexDirection: "column",
                    width: props.showCreateForm ? "60%" : "100%",
                  }}
                >
                  {pendingCreatedTasks
                    ? pendingCreatedTasks.items.map((x) => {
                        return (
                          <TaskDisplayPanelCreated
                            task={x}
                            key={x.id}
                            getActionButton={props.getActionButton}
                            getUser={props.getUser}
                            onEditTask={() => {
                              props.onSetEditTask(x)
                              props.onSetShowCreateForm(true)
                            }}
                          />
                        )
                      })
                    : null}
                </div>
              </div>
              {pendingCreatedTasks ? (
                <Paginator
                  first={
                    pendingCreatedTasks.pageSize *
                    (pendingCreatedTasks.pageNum - 1)
                  }
                  rows={pendingCreatedTasks.pageSize}
                  totalRecords={pendingCreatedTasks.numTotal}
                  onPageChange={onPendingPageChange}
                  rowsPerPageOptions={[5, 10, 20, 30]}
                />
              ) : null}
            </AccordionTab>
            <AccordionTab header="Completed tasks">
              <div style={{ flexDirection: "column", width: "100%" }}>
                {completedCreatedTasks
                  ? completedCreatedTasks.items.map((x) => {
                      return (
                        <TaskDisplayPanelCreated
                          task={x}
                          key={x.id}
                          getActionButton={props.getActionButton}
                          getUser={props.getUser}
                          onEditTask={() => {
                            props.onSetEditTask(x)
                            props.onSetShowCreateForm(true)
                          }}
                        />
                      )
                    })
                  : null}
              </div>
              {pendingCreatedTasks ? (
                <Paginator
                  first={
                    completedCreatedTasks.pageSize *
                    (completedCreatedTasks.pageNum - 1)
                  }
                  rows={completedCreatedTasks.pageSize}
                  totalRecords={completedCreatedTasks.numTotal}
                  onPageChange={onCompletedPageChange}
                  rowsPerPageOptions={[5, 10, 20, 30]}
                />
              ) : null}
            </AccordionTab>
          </Accordion>
        </div>
      )}
    </div>
  )
}
